import type { Feature, FeatureCollection } from '../generated';

/**
 * Page size for `GET /projects/{projectId}/features` (`size`) and the IN-filter
 * id batch. 100 × 36-char UUIDs is ~4.7KB of encoded query string — under the
 * common 8KB gateway limit.
 */
export const FEATURES_LIST_PAGE_SIZE = 100;

/** Splits `items` into consecutive batches of `size`. */
export const chunkItems = <T>(
  items: readonly T[],
  size: number
): readonly (readonly T[])[] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const toCount = (
  value: number | string | null | undefined,
  fallback: number
): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Walks `GET /features` pages until `total` features are collected.
 * `start` is the collection offset (`FeatureCollection.startIndex` + page length),
 * not a 1-based page number.
 */
export const collectFeaturePages = async (
  fetchPage: (start: number, size: number) => Promise<FeatureCollection>
): Promise<readonly Feature[]> => {
  const features: Feature[] = [];
  let start = 0;

  while (true) {
    const page = await fetchPage(start, FEATURES_LIST_PAGE_SIZE);
    const pageFeatures = page.features ?? [];
    features.push(...pageFeatures);

    const total = toCount(page.total, features.length);
    if (pageFeatures.length === 0 || features.length >= total) {
      break;
    }

    start = toCount(page.startIndex, start) + pageFeatures.length;
  }

  return features;
};
